import { useEffect, useMemo, useState } from "react";
import {
  updateProductStatus,
  updateProduct,
  getActiveStores,
} from "../../../components/products/functions";
import {
  createSerialDraft,
  createSerialFromDraft,
  updateSerial,
} from "../../../components/serials/functions";
import { validateProductAutographNotes } from "../../../components/utils/serialGradeFields";
import { getGradableModels } from "../../../components/gradableModels/functions";
import { getNextStatus, statusObject } from "../../../components/utils/allProductStatus";
import {
  gradingTypes,
  gradingTypeSupportsAutograph,
  gradingTypeSupportsGeneralScore,
  gradingTypeSupportsSubgrades,
  normalizeGradingType,
  priorityTypes,
} from "../../../components/utils/gradingTypes";
import {
  cashnator,
  decashnator,
  formatarData,
  isNumeric,
} from "../../../components/utils/functions";
import { getHolidays } from "../../../components/holidays/functions";
import {
  addBusinessDays,
  countBusinessDays,
  holidayYmdSet,
  isBusinessDay,
  parseLocalYmd,
  snapToBusinessDay,
  toLocalYmd,
} from "../../../components/utils/businessDays";
import MobileFilterSheet from "../../ui/MobileFilterSheet";
import MobileFormField from "../../ui/MobileFormField";

const SheetSection = ({ title, children }) => (
  <section className="mb-4 border-b border-primary/20 pb-3 last:border-b-0">
    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-primary">
      {title}
    </h3>
    {children}
  </section>
);

const inputClass =
  "w-full rounded-lg border border-primary/50 bg-monao px-3 py-2 text-sm text-laranjinha text-laranjinha";

export function MobileProductStatusSheet({
  open,
  onClose,
  product,
  user,
  serials,
  onSuccess,
  goodToast,
  badToast,
}) {
  const [status, setStatus] = useState(product?.status ?? "");
  const [notify, setNotify] = useState(true);
  const [returnTrackingCode, setReturnTrackingCode] = useState(
    product?.returnTrackingCode ?? ""
  );
  const [isHQ, setIsHQ] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !product) return;
    let next = getNextStatus(product.delivery, product.status, isHQ);
    if (product.status === "ANALYZING") {
      next = "ANALYZING";
    }
    setStatus(next ?? product.status);
    setReturnTrackingCode(product.returnTrackingCode ?? "");
    setNotify(true);
  }, [open, product, isHQ]);

  useEffect(() => {
    if (!open || !product?.store?._id) return;
    getActiveStores().then((data) => {
      if (data?.type === "success") {
        const store = data.data?.find((s) => s._id === product.store._id);
        if (store) setIsHQ(store.headquarter);
      }
    });
  }, [open, product?.store?._id]);

  const handleSave = async () => {
    if (!product?._id) return;
    setBusy(true);
    if (status === "GRADED") {
      if (serials.length < product.numberOfCards) {
        badToast("Seriais não preenchidos!");
        setBusy(false);
        return;
      }
      const autographNotesValidation = validateProductAutographNotes({
        ...product,
        serials,
      });
      if (!autographNotesValidation.ok) {
        badToast("Revise as notas de autógrafo antes de concluir a graduação.");
        setBusy(false);
        return;
      }
      const drafts = serials.filter((s) => s?.isDraft);
      for (let i = 0; i < drafts.length; i += 1) {
        const gradedData = await createSerialFromDraft(user?.token, drafts[i]?._id);
        if (gradedData?.type === "error") {
          badToast(gradedData?.message);
          setBusy(false);
          return;
        }
      }
    }
    const data = await updateProductStatus(
      user?.token,
      product._id,
      status,
      notify,
      statusObject[status],
      returnTrackingCode,
      user?.email
    );
    setBusy(false);
    if (data?.type === "success") {
      goodToast("Status do produto atualizado!");
      onSuccess();
      onClose();
      return;
    }
    badToast(data?.message ?? "Erro ao atualizar status");
  };

  const nextStatus = getNextStatus(product?.delivery, product?.status, isHQ);
  const disabled =
    busy ||
    (status === product?.status &&
      returnTrackingCode === (product?.returnTrackingCode ?? ""));

  return (
    <MobileFilterSheet open={open} onClose={onClose} title="Alterar status">
      {product?.status !== "ANALYZING" && nextStatus ? (
        <p className="mb-4 text-sm text-laranjinha/70">
          Próximo status do fluxo:{" "}
          <strong>{statusObject[nextStatus]}</strong>
        </p>
      ) : null}
      <MobileFormField label="Status">
        <select
          className={inputClass}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Status do pedido"
        >
          {Object.keys(statusObject).map((key) => (
            <option key={key} value={key}>{statusObject[key]}</option>
          ))}
        </select>
      </MobileFormField>
      {status === "RETURNED" ? (
        <MobileFormField label="Código de rastreio (retorno)">
          <input
            className={inputClass}
            type="text"
            value={returnTrackingCode}
            onChange={(e) => setReturnTrackingCode(e.target.value)}
            aria-label="Código de rastreio retorno"
          />
        </MobileFormField>
      ) : null}
      <label className="mb-4 flex items-center gap-2 text-sm text-laranjinha/80">
        <input
          type="checkbox"
          checked={notify}
          onChange={() => setNotify((v) => !v)}
        />
        Notificar usuário
      </label>
      <button
        type="button"
        disabled={disabled}
        onClick={handleSave}
        className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-primary font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50"
      >
        {busy ? "Salvando…" : "Salvar status"}
      </button>
    </MobileFilterSheet>
  );
}

export function MobileProductEditSheet({
  open,
  onClose,
  product,
  user,
  onSuccess,
  goodToast,
  badToast,
}) {
  const [gradingType, setGradingType] = useState("");
  const [numberOfCards, setNumberOfCards] = useState("");
  const [priority, setPriority] = useState("");
  const [deadline, setDeadline] = useState("");
  const [delivery, setDelivery] = useState("");
  const [finalPrice, setFinalPrice] = useState("R$0,00");
  const [cardsValue, setCardsValue] = useState("R$0,00");
  const [insurance, setInsurance] = useState("R$0,00");
  const [trackingCode, setTrackingCode] = useState("");
  const [autographEvaluation, setAutographEvaluation] = useState(false);
  const [busy, setBusy] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [promisedYmd, setPromisedYmd] = useState("");

  const holidaySet = useMemo(() => holidayYmdSet(holidays), [holidays]);
  const receivedDate = useMemo(() => {
    if (!product?.receivedAt) return null;
    const d = new Date(product.receivedAt);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }, [product?.receivedAt]);
  const canEditPromisedDate = !!receivedDate;

  useEffect(() => {
    if (!open || !product) return;
    setGradingType(normalizeGradingType(product.gradingType) || "");
    setNumberOfCards(String(product.numberOfCards ?? ""));
    setPriority(product.priority ?? "");
    setDeadline(product.deadline ?? "");
    setDelivery(product.delivery ?? "");
    setFinalPrice(cashnator(product.finalPrice) || "R$0,00");
    setCardsValue(cashnator(product.cardsValue) || "R$0,00");
    setInsurance(cashnator(product.insurance));
    setTrackingCode(product.trackingCode ?? "");
    setAutographEvaluation(!!product.autographEvaluation);
    setPromisedYmd("");
  }, [open, product]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const data = await getHolidays(user?.token);
      if (cancelled) return;
      if (data?.type !== "success") {
        badToast("Não foi possível carregar feriados; prazo usa só fins de semana");
        setHolidays([]);
        return;
      }
      setHolidays(data?.data || []);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.token]);

  useEffect(() => {
    if (!open) return;
    if (!canEditPromisedDate) {
      setPromisedYmd("");
      return;
    }
    const n =
      deadline === "" || deadline === null || deadline === undefined
        ? null
        : Number(deadline);
    if (n === null || Number.isNaN(n)) {
      setPromisedYmd("");
      return;
    }
    const end = addBusinessDays(receivedDate, n, holidaySet);
    setPromisedYmd(toLocalYmd(end) || "");
  }, [open, holidaySet, receivedDate, canEditPromisedDate, deadline]);

  const handleDeadline = (value) => {
    if (!value) {
      setDeadline("");
      setPromisedYmd("");
      return;
    }
    if (!isNumeric(value)) return;
    setDeadline(value);
    if (!canEditPromisedDate) return;
    const end = addBusinessDays(receivedDate, Number(value), holidaySet);
    setPromisedYmd(toLocalYmd(end) || "");
  };

  const handlePromisedDate = (ymd) => {
    if (!canEditPromisedDate) return;
    if (!ymd) {
      if (deadline === "" || deadline === null) {
        setPromisedYmd("");
        return;
      }
      const end = addBusinessDays(receivedDate, Number(deadline), holidaySet);
      setPromisedYmd(toLocalYmd(end) || "");
      return;
    }
    let picked = parseLocalYmd(ymd);
    if (!picked) return;

    if (toLocalYmd(picked) < toLocalYmd(receivedDate)) {
      badToast("A data prometida não pode ser anterior à data de recebimento");
      return;
    }

    if (!isBusinessDay(picked, holidaySet)) {
      const snapped = snapToBusinessDay(picked, holidaySet);
      goodToast(`Data não é dia útil; ajustada para ${formatarData(snapped)}`);
      picked = snapped;
    }

    const n = countBusinessDays(receivedDate, picked, holidaySet);
    if (n < 0) {
      badToast("A data prometida não pode ser anterior à data de recebimento");
      return;
    }
    setDeadline(String(n));
    setPromisedYmd(toLocalYmd(picked));
  };

  const handleSave = async () => {
    if (!product?._id) return;
    setBusy(true);
    const body = {
      gradingType,
      numberOfCards: numberOfCards,
      priority,
      deadline,
      delivery,
      finalPrice: decashnator(finalPrice),
      cardsValue: decashnator(cardsValue),
      insurance: decashnator(insurance),
      trackingCode,
      userEmail: user?.email,
      autographEvaluation:
        gradingTypeSupportsAutograph(gradingType) && autographEvaluation,
      ...(product.store?._id ? { store: product.store._id } : {}),
    };
    const data = await updateProduct(user?.token, body, product._id);
    setBusy(false);
    if (data?.type === "success") {
      goodToast("Pedido atualizado!");
      onSuccess();
      onClose();
      return;
    }
    badToast("Erro ao atualizar pedido");
  };

  return (
    <MobileFilterSheet open={open} onClose={onClose} title="Editar pedido">
      <SheetSection title="Serviço">
        <MobileFormField label="Tipo de grading">
          <select
            className={inputClass}
            value={gradingType}
            onChange={(e) => {
              const next = e.target.value;
              setGradingType(next);
              if (!gradingTypeSupportsAutograph(next)) {
                setAutographEvaluation(false);
              }
            }}
            aria-label="Tipo de grading"
          >
            {Object.keys(gradingTypes).map((key) => (
              <option key={key} value={key}>{gradingTypes[key]}</option>
            ))}
          </select>
        </MobileFormField>
        {gradingTypeSupportsAutograph(gradingType) ? (
          <label className="mb-4 flex items-center gap-2 text-sm text-laranjinha/80">
            <input
              type="checkbox"
              checked={autographEvaluation}
              onChange={() => setAutographEvaluation((v) => !v)}
            />
            Avaliação de autógrafo
          </label>
        ) : null}
        <MobileFormField label="Nº de itens">
          <input
            className={inputClass}
            type="text"
            value={numberOfCards}
            onChange={(e) => {
              const v = e.target.value;
              if (!v || isNumeric(v)) setNumberOfCards(v);
            }}
            aria-label="Número de itens"
          />
        </MobileFormField>
        <MobileFormField label="Prioridade">
          <select
            className={inputClass}
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            aria-label="Prioridade"
          >
            {Object.keys(priorityTypes).map((key) => (
              <option key={key} value={key}>{priorityTypes[key]}</option>
            ))}
          </select>
        </MobileFormField>
      </SheetSection>

      <SheetSection title="Prazo">
        <MobileFormField label="Dias úteis">
          <input
            className={inputClass}
            type="text"
            value={deadline}
            onChange={(e) => handleDeadline(e.target.value)}
            aria-label="Dias úteis"
          />
        </MobileFormField>
        <MobileFormField label="Data prometida">
          <input
            className={`${inputClass} disabled:opacity-50`}
            type="date"
            value={promisedYmd}
            disabled={!canEditPromisedDate}
            onChange={(e) => handlePromisedDate(e.target.value)}
            aria-label="Data prometida"
          />
        </MobileFormField>
        {!canEditPromisedDate ? (
          <p className="mb-4 text-sm text-laranjinha/60">
            Informe a data de recebimento (status na fila) para calcular a data prometida
          </p>
        ) : null}
      </SheetSection>

      <SheetSection title="Valores">
        <MobileFormField label="Preço final">
          <input
            className={inputClass}
            type="text"
            value={finalPrice}
            onChange={(e) => {
              const nv = decashnator(e.target.value);
              if (isNumeric(nv)) setFinalPrice(cashnator(nv));
            }}
            aria-label="Preço final"
          />
        </MobileFormField>
        <MobileFormField label="Valor itens">
          <input
            className={inputClass}
            type="text"
            value={cardsValue}
            onChange={(e) => {
              const nv = decashnator(e.target.value);
              if (isNumeric(nv)) setCardsValue(cashnator(nv));
            }}
            aria-label="Valor itens"
          />
        </MobileFormField>
        <MobileFormField label="Taxa de manuseio">
          <input
            className={inputClass}
            type="text"
            value={insurance}
            onChange={(e) => {
              const nv = decashnator(e.target.value);
              if (isNumeric(nv)) setInsurance(cashnator(nv));
            }}
            aria-label="Taxa de manuseio"
          />
        </MobileFormField>
      </SheetSection>

      <SheetSection title="Entrega">
        <MobileFormField label="Entrega">
          <select
            className={inputClass}
            value={delivery}
            onChange={(e) => setDelivery(e.target.value)}
            aria-label="Entrega"
          >
            <option value="presencial">Presencial</option>
            <option value="correios">Correios</option>
          </select>
        </MobileFormField>
        {delivery === "correios" ? (
          <MobileFormField label="Rastreio">
            <input
              className={inputClass}
              type="text"
              value={trackingCode}
              onChange={(e) => setTrackingCode(e.target.value)}
              aria-label="Código de rastreio"
            />
          </MobileFormField>
        ) : null}
      </SheetSection>

      <button
        type="button"
        disabled={busy}
        onClick={handleSave}
        className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-primary font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50"
      >
        {busy ? "Salvando…" : "Salvar alterações"}
      </button>
    </MobileFilterSheet>
  );
}

export function MobileSerialCreateSheet({
  open,
  onClose,
  product,
  user,
  onSuccess,
  goodToast,
  badToast,
}) {
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState("");
  const [generalScore, setGeneralScore] = useState("");
  const [surface, setSurface] = useState("");
  const [edges, setEdges] = useState("");
  const [centering, setCentering] = useState("");
  const [corners, setCorners] = useState("");
  const [notaAutografo, setNotaAutografo] = useState("");
  const [busy, setBusy] = useState(false);

  const supportsGeneralScore = gradingTypeSupportsGeneralScore(product?.gradingType);
  const supportsSubgrades = gradingTypeSupportsSubgrades(
    product?.gradingType,
    product?.preLacreFormat
  );
  const supportsAutographNote = product?.autographEvaluation === true;

  useEffect(() => {
    if (!open) return;
    setModelId("");
    setGeneralScore("");
    setSurface("");
    setEdges("");
    setCentering("");
    setCorners("");
    setNotaAutografo("");
    getGradableModels(user?.token).then((data) => {
      if (data?.type === "success") setModels(data.data ?? []);
    });
  }, [open, user?.token]);

  const disableSave =
    !modelId ||
    busy ||
    (supportsGeneralScore && generalScore === "") ||
    (supportsSubgrades &&
      (surface === "" || edges === "" || centering === "" || corners === "")) ||
    (supportsAutographNote && notaAutografo === "");

  const handleSave = async () => {
    if (!product?._id) return;
    setBusy(true);
    const body = {
      gradableModelId: modelId,
      productId: product._id,
      ...(supportsGeneralScore ? { generalScore } : {}),
      ...(supportsSubgrades ? { surface, edges, centering, corners } : {}),
      ...(supportsAutographNote ? { notaAutografo } : {}),
    };
    const data = await createSerialDraft(user?.token, product._id, body);
    setBusy(false);
    if (data?.type === "success") {
      goodToast(data?.message ?? "Serial cadastrado!");
      onSuccess();
      onClose();
      return;
    }
    badToast(data?.message ?? "Erro ao cadastrar serial");
  };

  return (
    <MobileFilterSheet open={open} onClose={onClose} title="Cadastrar serial">
      <MobileFormField label="Modelo gradável">
        <select
          className={inputClass}
          value={modelId}
          onChange={(e) => setModelId(e.target.value)}
          aria-label="Modelo gradável"
        >
          <option value="">Selecione…</option>
          {models.map((m) => (
            <option key={m._id} value={m._id}>
              {m.game} — {m.name} ({m.numbering})
            </option>
          ))}
        </select>
      </MobileFormField>
      {supportsGeneralScore ? (
        <MobileFormField label="Nota geral">
          <input
            className={inputClass}
            type="text"
            value={generalScore}
            onChange={(e) => setGeneralScore(e.target.value)}
            aria-label="Nota geral"
          />
        </MobileFormField>
      ) : null}
      {supportsSubgrades ? (
        <>
          <MobileFormField label="Surface">
            <input className={inputClass} value={surface} onChange={(e) => setSurface(e.target.value)} aria-label="Surface" />
          </MobileFormField>
          <MobileFormField label="Edges">
            <input className={inputClass} value={edges} onChange={(e) => setEdges(e.target.value)} aria-label="Edges" />
          </MobileFormField>
          <MobileFormField label="Centering">
            <input className={inputClass} value={centering} onChange={(e) => setCentering(e.target.value)} aria-label="Centering" />
          </MobileFormField>
          <MobileFormField label="Corners">
            <input className={inputClass} value={corners} onChange={(e) => setCorners(e.target.value)} aria-label="Corners" />
          </MobileFormField>
        </>
      ) : null}
      {supportsAutographNote ? (
        <MobileFormField label="Nota do autógrafo">
          <select
            className={inputClass}
            value={notaAutografo}
            onChange={(e) => setNotaAutografo(e.target.value)}
            aria-label="Nota do autógrafo"
          >
            <option value="">Selecione</option>
            <option value="Autentico">Autentico</option>
            <option value="8">8</option>
            <option value="9">9</option>
            <option value="10">10</option>
          </select>
        </MobileFormField>
      ) : null}
      <button
        type="button"
        disabled={disableSave}
        onClick={handleSave}
        className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-green-700 font-medium text-white hover:bg-green-800 disabled:opacity-50"
      >
        {busy ? "Salvando…" : "Salvar serial"}
      </button>
    </MobileFilterSheet>
  );
}

export function MobileSerialEditSheet({
  open,
  onClose,
  serial,
  product,
  user,
  onSuccess,
  goodToast,
  badToast,
}) {
  const [generalScore, setGeneralScore] = useState("");
  const [surface, setSurface] = useState("");
  const [edges, setEdges] = useState("");
  const [centering, setCentering] = useState("");
  const [corners, setCorners] = useState("");
  const [notaAutografo, setNotaAutografo] = useState("");
  const [busy, setBusy] = useState(false);

  const supportsGeneralScore = gradingTypeSupportsGeneralScore(product?.gradingType);
  const supportsSubgrades = gradingTypeSupportsSubgrades(
    product?.gradingType,
    product?.preLacreFormat
  );
  const supportsAutographNote = product?.autographEvaluation === true;

  useEffect(() => {
    if (!open || !serial) return;
    setGeneralScore(serial.generalScore ?? "");
    setSurface(serial.surface ?? "");
    setEdges(serial.edges ?? "");
    setCentering(serial.centering ?? "");
    setCorners(serial.corners ?? "");
    setNotaAutografo(serial.notaAutografo ?? "");
  }, [open, serial]);

  const handleSave = async () => {
    if (!serial?._id) return;
    setBusy(true);
    const body = {};
    if (supportsGeneralScore && generalScore !== serial.generalScore) {
      body.generalScore = generalScore;
    }
    if (supportsSubgrades) {
      if (surface !== serial.surface) body.surface = surface;
      if (edges !== serial.edges) body.edges = edges;
      if (centering !== serial.centering) body.centering = centering;
      if (corners !== serial.corners) body.corners = corners;
    }
    if (supportsAutographNote && notaAutografo !== (serial.notaAutografo ?? "")) {
      body.notaAutografo = notaAutografo;
    }
    if (!Object.keys(body).length) {
      setBusy(false);
      onClose();
      return;
    }
    const data = await updateSerial(user?.token, serial._id, body);
    setBusy(false);
    if (data?.type === "success") {
      goodToast("Serial atualizado!");
      onSuccess();
      onClose();
      return;
    }
    badToast(data?.message ?? "Erro ao atualizar serial");
  };

  if (!serial) return null;

  return (
    <MobileFilterSheet
      open={open}
      onClose={onClose}
      title={`Editar serial ${serial.serialNumber ?? ""}`}
    >
      {supportsGeneralScore ? (
        <MobileFormField label="Nota geral">
          <input
            className={inputClass}
            type="text"
            value={generalScore}
            onChange={(e) => setGeneralScore(e.target.value)}
            aria-label="Nota geral"
          />
        </MobileFormField>
      ) : null}
      {supportsSubgrades ? (
        <>
          <MobileFormField label="Surface">
            <input className={inputClass} value={surface} onChange={(e) => setSurface(e.target.value)} aria-label="Surface" />
          </MobileFormField>
          <MobileFormField label="Edges">
            <input className={inputClass} value={edges} onChange={(e) => setEdges(e.target.value)} aria-label="Edges" />
          </MobileFormField>
          <MobileFormField label="Centering">
            <input className={inputClass} value={centering} onChange={(e) => setCentering(e.target.value)} aria-label="Centering" />
          </MobileFormField>
          <MobileFormField label="Corners">
            <input className={inputClass} value={corners} onChange={(e) => setCorners(e.target.value)} aria-label="Corners" />
          </MobileFormField>
        </>
      ) : null}
      {supportsAutographNote ? (
        <MobileFormField label="Nota do autógrafo">
          <select
            className={inputClass}
            value={notaAutografo}
            onChange={(e) => setNotaAutografo(e.target.value)}
            aria-label="Nota do autógrafo"
          >
            <option value="">Selecione</option>
            <option value="Autentico">Autentico</option>
            <option value="8">8</option>
            <option value="9">9</option>
            <option value="10">10</option>
          </select>
        </MobileFormField>
      ) : null}
      <button
        type="button"
        disabled={busy}
        onClick={handleSave}
        className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-primary font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50"
      >
        {busy ? "Salvando…" : "Salvar notas"}
      </button>
    </MobileFilterSheet>
  );
}
